import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { downloadCvPdf, downloadCvDocx } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { FilePlus2, MoreVertical, Loader2, FileText, Copy, Trash2, ExternalLink, Files, Download, FileType, Lock, Send } from "lucide-react";

const STATUS = {
  draft: { label: "Utkast", cls: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]" },
  final: { label: "Godkänd", cls: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]" },
};

export default function MinaCV() {
  const [cvs, setCvs] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/cvs").then((r) => setCvs(r.data));
  useEffect(() => { load(); }, []);

  const duplicate = async (id) => {
    const { data } = await api.post(`/cvs/${id}/duplicate`);
    toast.success("Ny version skapad");
    navigate(`/app/cv/${data.id}`);
  };
  const remove = async (id) => { await api.delete(`/cvs/${id}`); toast.success("CV raderat"); load(); };
  const pdf = async (cv) => {
    const t = toast.loading("Skapar PDF…");
    try {
      await downloadCvPdf(cv.id, `HAMN_CV_${(cv.company || "CV")}-${(cv.title || "")}`.replace(/\s+/g, "_") + ".pdf");
      toast.success("PDF nedladdad", { id: t });
    } catch { toast.error("Kunde inte ladda ner PDF", { id: t }); }
  };
  const docx = async (cv) => {
    const t = toast.loading("Skapar Word-fil…");
    try {
      await downloadCvDocx(cv.id, `HAMN_CV_${(cv.company || "CV")}-${(cv.title || "")}`.replace(/\s+/g, "_") + ".docx");
      toast.success("Word nedladdad", { id: t });
    } catch { toast.error("Kunde inte ladda ner Word", { id: t }); }
  };

  if (cvs === null) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" /></div>;

  return (
    <div>
      <PageHeader
        title="Mina CV:n"
        description="Varje CV sparas som en egen version kopplad till ett jobb. Din Masterprofil påverkas aldrig av att du redigerar ett CV."
        action={<Link to="/app/skapa-cv"><Button data-testid="create-cv-btn" className="rounded-full"><FilePlus2 className="h-4 w-4 mr-1.5" /> Skapa CV</Button></Link>}
      />

      {cvs.length === 0 ? (
        <Card className="rounded-2xl border-dashed border-2 border-border p-12 text-center">
          <Files className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">Du har inga CV:n ännu.</p>
          <Link to="/app/skapa-cv"><Button className="rounded-full mt-4"><FilePlus2 className="h-4 w-4 mr-1.5" /> Skapa ditt första CV</Button></Link>
        </Card>
      ) : (
        <div className="grid gap-4">
          {cvs.map((cv) => {
            const st = STATUS[cv.status] || STATUS.draft;
            return (
              <Card key={cv.id} data-testid={`cv-${cv.id}`} className="rounded-2xl border-border p-5 flex items-center justify-between gap-4 transition-transform hover:-translate-y-0.5">
                <Link to={`/app/cv/${cv.id}`} className="flex items-center gap-4 min-w-0 flex-1">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary shrink-0"><FileText className="h-5 w-5" /></div>
                  <div className="min-w-0">
                    <div className="font-head font-semibold text-lg truncate">{cv.company || "Företag"} – {cv.title || "Tjänst"}</div>
                    <div className="text-sm text-muted-foreground">{new Date(cv.created_at).toLocaleDateString("sv-SE")} · Mall: {cv.template}</div>
                  </div>
                </Link>
                <div className="flex items-center gap-2 shrink-0">
                  {cv.locked && (
                    <Badge data-testid={`cv-locked-badge-${cv.id}`} className="rounded-full border-0 bg-amber-100 text-amber-800 gap-1">
                      <Lock className="h-3 w-3" /> Låst
                    </Badge>
                  )}
                  <Badge className={`rounded-full border-0 ${st.cls}`}>{st.label}</Badge>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button data-testid={`cv-menu-${cv.id}`} variant="ghost" size="icon"><MoreVertical className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/app/cv/${cv.id}`)}><ExternalLink className="h-4 w-4 mr-2" /> Öppna & redigera</DropdownMenuItem>
                      {cv.apply_url && (
                        <DropdownMenuItem data-testid={`apply-${cv.id}`} onClick={() => window.open(cv.apply_url, "_blank", "noopener")}><Send className="h-4 w-4 mr-2" /> Ansök hos arbetsgivaren</DropdownMenuItem>
                      )}
                      <DropdownMenuItem data-testid={`pdf-${cv.id}`} onClick={() => pdf(cv)}><Download className="h-4 w-4 mr-2" /> Ladda ner PDF</DropdownMenuItem>
                      <DropdownMenuItem data-testid={`docx-${cv.id}`} onClick={() => docx(cv)}><FileType className="h-4 w-4 mr-2" /> Ladda ner Word</DropdownMenuItem>
                      <DropdownMenuItem data-testid={`duplicate-${cv.id}`} onClick={() => duplicate(cv.id)}><Copy className="h-4 w-4 mr-2" /> Duplicera / ny version</DropdownMenuItem>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-destructive"><Trash2 className="h-4 w-4 mr-2" /> Radera</DropdownMenuItem>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Radera CV?</AlertDialogTitle>
                            <AlertDialogDescription>Detta CV tas bort permanent. Din Masterprofil påverkas inte.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Avbryt</AlertDialogCancel>
                            <AlertDialogAction data-testid={`confirm-delete-cv-${cv.id}`} onClick={() => remove(cv.id)}>Radera</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
